import { SECTION_CARD } from '@/components/ui/section-card-variants'
import Skeleton from '@/components/ui/Skeleton'
import { STOCKS_TRADED_SIDES_AVAILABLE } from '@/features/brokers/api/stocksTradedSides'
import { cn } from '@/lib/utils'
import {
  FLOOR_CELL,
  FLOOR_STATS_GRID,
  FLOOR_STATS_PANEL,
  SECTOR_COLUMNS,
  STOCK_COLUMNS,
  TOP_BROKER_CELL,
  TOP_BROKER_ROW,
  TOP_BROKER_VALUE,
  TRADED_HEAD,
  TRADED_PAIR,
  TRADED_TABLE,
  floorCellRules,
} from '@/screens/brokers/components/overview/overview-layout'

/*
 * Brokers · Overview before its first response: the LOADED board, block for block, in its order
 * and at its heights — "match the loaded layout, not a fabricated loading frame". Every block
 * reuses the loaded block's own boxes (`overview-layout.ts`), so it matches at every width.
 *
 *   floor stats        the four-cell panel, label and figure bars.
 *   top brokers        head (title + two tabs), caption, six 28px rows.
 *   traded-most pair   both titles and table cards with their head bands; three sector rows
 *                      beside five stock rows (or the stock card's not-available body).
 *
 * Bars only, no words, so nothing here can be mistaken for, or found by a test as, the loaded
 * content.
 */

/** The floor stats panel: four cells of label and figure, ruled as the loaded strip. */
export function FloorStatsSkeleton() {
  return (
    <div className={FLOOR_STATS_PANEL}>
      <div className={FLOOR_STATS_GRID}>
        {Array.from({ length: 4 }, (_, index) => (
          <div key={index} className={cn(FLOOR_CELL, floorCellRules(index))}>
            <span className="flex h-4 items-center">
              <Skeleton className="h-3 w-16 rounded-xs" />
            </span>
            <span className="flex h-6 items-center">
              <Skeleton className="h-4 w-24 rounded-xs" />
            </span>
          </div>
        ))}
      </div>
    </div>
  )
}

/** Top brokers' rows: mark, name, track and figure on the loaded row's own grid. */
export function TopBrokerRowsSkeleton({ rows }: { rows: number }) {
  return (
    <div className="flex flex-col gap-2">
      {Array.from({ length: rows }, (_, index) => (
        <div key={index} className={TOP_BROKER_ROW}>
          <span className={cn(TOP_BROKER_CELL, 'gap-2')}>
            <Skeleton className="size-6 shrink-0 rounded-full" />
            <Skeleton className="h-3 w-20 rounded-xs" />
          </span>
          <Skeleton className="h-2 min-w-0 flex-1 rounded-none" />
          <span className={cn(TOP_BROKER_VALUE, 'flex justify-end')}>
            <Skeleton className="h-3 w-12 rounded-xs" />
          </span>
        </div>
      ))}
    </div>
  )
}

/** A traded-most table's rows: one bar per column on the loaded rows' grid. */
export function TradedRowsSkeleton({ rows, columns }: { rows: number; columns: string }) {
  const cells = columns === STOCK_COLUMNS ? ['w-3', 'w-20', 'w-20'] : ['w-28', 'w-full', 'w-14']
  return (
    <div className="flex flex-1 flex-col px-4">
      {Array.from({ length: rows }, (_, index) => (
        <div
          key={index}
          className={cn(
            'grid min-h-13 flex-1 items-center gap-x-6 py-3',
            columns,
            index === 0 ? null : 'border-t border-border-subtle',
          )}
        >
          {cells.map((width, cell) => (
            <span
              key={cell}
              className={cn('flex', cell === cells.length - 1 ? 'justify-end' : null)}
            >
              <Skeleton className={cn('h-3 rounded-xs', width)} />
            </span>
          ))}
        </div>
      ))}
    </div>
  )
}

/** The title above, then the bordered card and its head band. */
function TradedTableSkeleton({ rows, columns }: { rows: number; columns: string }) {
  return (
    <div className="flex min-w-0 flex-col gap-3">
      <span className="flex h-6 items-center">
        <Skeleton className="h-4 w-40 rounded-xs" />
      </span>
      <div className={TRADED_TABLE}>
        <div className={cn(TRADED_HEAD, columns)}>
          <span className="flex h-4 items-center">
            <Skeleton className="h-3 w-12 rounded-xs" />
          </span>
        </div>
        {rows > 0 ? (
          <TradedRowsSkeleton rows={rows} columns={columns} />
        ) : (
          <div className="min-h-52" />
        )}
      </div>
    </div>
  )
}

/** Top brokers: the head (title, two tabs), the caption, then six rows. */
function TopBrokersSkeleton() {
  return (
    <div className={SECTION_CARD}>
      <div className="flex min-h-9 items-center gap-3">
        <div className="min-w-0 flex-1">
          <Skeleton className="h-4 w-40 rounded-xs" />
        </div>
        <div className="flex shrink-0 items-center gap-5">
          <Skeleton className="h-4 w-20 rounded-xs" />
          <Skeleton className="h-4 w-20 rounded-xs" />
        </div>
      </div>
      <div className="mt-3 flex h-4 items-center">
        <Skeleton className="h-3 w-44 rounded-xs" />
      </div>
      <div className="mt-3">
        <TopBrokerRowsSkeleton rows={6} />
      </div>
    </div>
  )
}

export function OverviewBoardSkeleton() {
  return (
    <>
      <FloorStatsSkeleton />
      <TopBrokersSkeleton />
      <div className={TRADED_PAIR}>
        <TradedTableSkeleton rows={3} columns={SECTOR_COLUMNS} />
        <TradedTableSkeleton rows={STOCKS_TRADED_SIDES_AVAILABLE ? 5 : 0} columns={STOCK_COLUMNS} />
      </div>
    </>
  )
}
